import { Select, SelectItem } from '@heroui/react';
import _ from 'lodash';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetchVisibleAccountsQuery } from '@/slices/account-api-slice.ts';
import type { AccountListModel } from '../../types/account.type.ts';
import type { FormControlProps } from '../../types/form-props.type';

interface AccountSelectorProps extends FormControlProps {
  organizationKey?: string;
  blackList?: AccountListModel[];
}

const AccountSelector: FC<AccountSelectorProps> = ({
  name,
  label = 'Account',
  displayProperty = 'accountName',
  disabled,
  required,
  organizationKey,
  blackList,
}) => {
  const { accounts, isLoading } = useFetchVisibleAccountsQuery(
    { organizationKey },
    {
      selectFromResult: ({ data, isLoading }) => ({
        accounts: data?.filter(x => (blackList ?? []).every(y => y.id !== x.id)),
        isLoading,
      }),
    },
  );
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? 'This field is required' : undefined }}
      render={({ field, fieldState }) => (
        <Select
          label={label}
          placeholder={`Select ${label}`}
          variant='bordered'
          isRequired={required}
          isDisabled={disabled || isLoading}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          items={accounts || []}
          selectedKeys={field.value?.id ? new Set([field.value.id.toString()]) : new Set([])}
          onSelectionChange={keys => {
            const selectedId = Array.from(keys)[0];
            if (selectedId) {
              const selectedAccount = accounts?.find(x => x.id === parseInt(selectedId as string));
              field.onChange(selectedAccount);
            } else {
              field.onChange(null);
            }
          }}
        >
          {(item: AccountListModel) => (
            <SelectItem key={item.id.toString()} textValue={_.get(item, displayProperty)}>
              {_.get(item, displayProperty)}
            </SelectItem>
          )}
        </Select>
      )}
    />
  );
};

export default AccountSelector;
